import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../lib/cn';

export type ButtonVariant = 'primary' | 'strong' | 'outline' | 'ghost' | 'danger' | 'danger-outline';
export type ButtonSize = 'sm' | 'md' | 'lg';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-primary-ink hover:brightness-95 active:brightness-95',
  strong: 'bg-strong text-strong-ink hover:opacity-90',
  outline: 'border border-border bg-card text-ink hover:bg-surface-alt',
  ghost: 'text-ink-2 hover:bg-surface hover:text-ink',
  danger: 'bg-danger text-white hover:brightness-110',
  'danger-outline': 'border border-border bg-card text-danger-ink hover:bg-danger-soft',
};
// Touch devices always get ≥ 44 px targets (§11b); desktop matches the 40 px buttons in S1.
const sizes: Record<ButtonSize, string> = {
  sm: 'h-11 px-3 text-sm pointer-fine:h-9',
  md: 'h-11 px-4 text-[15px] pointer-fine:h-10',
  lg: 'h-12 px-5 text-[15px] font-semibold',
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  block?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'outline', size = 'md', loading, icon, block, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-2 rounded-lg font-medium whitespace-nowrap transition-[background-color,filter,opacity] duration-150',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variants[variant],
        sizes[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 aria-hidden className="size-4 animate-spin" /> : icon}
      {children}
    </button>
  );
});

export type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  children: ReactNode;
  variant?: 'ghost' | 'outline';
  badge?: number | boolean;
};

/** Icon-only button: always 44×44, always labelled. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, children, variant = 'ghost', badge, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'relative inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-ink-2 transition-colors duration-150 disabled:opacity-50',
        variant === 'ghost' ? 'hover:bg-surface hover:text-ink' : 'border border-border bg-card hover:bg-surface-alt',
        className,
      )}
      {...rest}
    >
      {children}
      {badge ? (
        <span
          aria-hidden
          className="absolute top-1.5 right-1.5 flex min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] leading-4 font-semibold text-white ring-2 ring-card"
        >
          {typeof badge === 'number' ? (badge > 99 ? '99+' : badge) : ''}
        </span>
      ) : null}
    </button>
  );
});
